import { type Field, field } from "../../design-system/components/field.ts";
import { el } from "../../design-system/dom.ts";
import {
  type RecipientFieldName,
  type RecipientRequirements,
  type RecipientValues,
  recipientField,
} from "./recipient-requirements.ts";

/**
 * The recipient-information form, rendered from configuration.
 *
 * ## One renderer, every institution
 *
 * This module reads `RecipientRequirements` and draws it. It does not know which
 * bank it is drawing for, contains no tenant identifier, and has no branch that
 * could acquire one — change a tenant's requirement set and this file does not
 * change. That is the whole point of the correction this milestone implements:
 * the sending bank decides what it needs, and Nura ships one renderer rather
 * than one universal form.
 *
 * ## Where the values live, and for how long
 *
 * In a `Map` on this object, and nowhere else. They are not written to storage,
 * not put in the URL, not logged, not sent anywhere, and not copied into the
 * flow, the provider or the destination. `clear()` empties the map **and** the
 * inputs, and the claimant surface calls it when the form is submitted and again
 * when the surface is destroyed — so closing the reference experience leaves
 * nothing behind. `frontend/test/recipient-privacy.test.ts` holds all of that.
 *
 * In production the values may not exist here at all: under a bank-controlled
 * collection component Nura renders no field, and this module is simply not the
 * collector. The port in `claim-experience.ts` is shaped so that either is a
 * drop-in.
 *
 * ## Validation timing
 *
 * On blur, not on keystroke — the UI UX Pro Max `inline-validation` rule: telling
 * someone their IBAN is invalid while they are still typing the second character
 * is noise. Once a field is showing an error it re-validates on input, so the
 * error clears the moment it is fixed rather than at the next blur.
 *
 * The Continue button is never disabled. A disabled primary action with no
 * explanation is a dead end; pressing it validates everything, shows every
 * message at once, and moves focus to the first field that needs attention.
 */

export interface RecipientFormOptions {
  readonly requirements: RecipientRequirements;
  /** Called when a field changes, so the surface can clear a stale summary. */
  readonly onChange?: () => void;
}

export interface RecipientForm {
  readonly root: HTMLElement;
  /** Validate everything; show messages; focus the first field needing attention. */
  validate(): boolean;
  /** The values entered so far. A snapshot — the caller receives a copy. */
  values(): RecipientValues;
  /** Empty the map and the inputs. Called on submit and on teardown. */
  clear(): void;
}

interface Entry {
  readonly name: RecipientFieldName;
  readonly required: boolean;
  readonly control: Field;
}

export function recipientForm(options: RecipientFormOptions): RecipientForm {
  const values = new Map<RecipientFieldName, string>();
  const entries: Entry[] = [];

  for (const requirement of options.requirements.fields) {
    const definition = recipientField(requirement.name);
    let showingError = false;

    const control = field({
      label: requirement.required ? definition.label : `${definition.label} (optional)`,
      type: definition.type,
      inputMode: definition.inputMode,
      required: requirement.required,
      help: definition.help,
      // Present only where the definition says it is safe. The IBAN and the
      // account number deliberately carry none.
      ...(definition.autocomplete === undefined ? {} : { autocomplete: definition.autocomplete }),
      onInput: (value: string) => {
        values.set(requirement.name, value);
        options.onChange?.();
        if (showingError) {
          const message = check(requirement.required, definition.validate, value);
          if (message === undefined) {
            showingError = false;
            control.setError(undefined);
          }
        }
      },
    });

    control.input.addEventListener("blur", () => {
      const value = control.input.value;
      // An untouched optional field should not be scolded for being empty.
      if (value.trim() === "" && !requirement.required) return;
      if (value.trim() === "" && !showingError) return;
      const message = check(requirement.required, definition.validate, value);
      showingError = message !== undefined;
      control.setError(message);
    });

    entries.push({ name: requirement.name, required: requirement.required, control });
  }

  function check(
    isRequired: boolean,
    validate: (value: string) => string | undefined,
    value: string,
  ): string | undefined {
    if (value.trim() === "") return isRequired ? "This is needed to send your money." : undefined;
    return validate(value);
  }

  function validate(): boolean {
    let firstInvalid: Field | undefined;

    for (const entry of entries) {
      const definition = recipientField(entry.name);
      const message = check(entry.required, definition.validate, entry.control.input.value);
      entry.control.setError(message);
      if (message !== undefined && firstInvalid === undefined) firstInvalid = entry.control;
    }

    if (firstInvalid !== undefined) {
      // The UI UX Pro Max `focus-management` rule: send the person to the field
      // that needs them, not to the top of a page they have already read.
      firstInvalid.input.focus({ preventScroll: false });
      return false;
    }
    return true;
  }

  return {
    root: el(
      "div",
      { class: "nura-recipient-form" },
      entries.map((entry) => entry.control.root),
    ),

    validate,

    values(): RecipientValues {
      return new Map(values);
    },

    clear(): void {
      values.clear();
      for (const entry of entries) {
        entry.control.input.value = "";
        entry.control.setError(undefined);
      }
    },
  };
}
